// Restore-time remap of index-keyed session maps (audit high, session.ts).
//
// assetBlocks / stsAssets / customDurations / customVideoEnabled / freeMode /
// freeBlocks / freeActiveIdx are keyed by the COMBINED asset index
// (config.assets followed by customAssets) and persisted that way in
// IndexedDB. The config, however, is fetched fresh from the server on every
// load — if the admin inserted, reordered, or deleted an asset while a
// (multi-day) talent session was parked in IDB, every restored index would
// point at the wrong asset: takes appear under the wrong script, uploads
// ship with the wrong title.
//
// Fix: the stored record carries `assetIdOrder` — the combined asset-id
// list at save time. On restore we diff it against the current combined
// order and re-key all index maps by asset id. State of vanished assets is
// dropped; currentAssetIdx follows its asset (or clamps to 0).
//
// `versions` is already keyed by assetId and passes through untouched.

import type { SessionState } from '@shared/types';

/** oldIdx → newIdx for every asset id present in both orders. */
export function buildIndexMapping(
  oldOrder: string[],
  newOrder: string[],
): Map<number, number> {
  const newIdxById = new Map(newOrder.map((id, i) => [id, i] as const));
  const m = new Map<number, number>();
  oldOrder.forEach((id, oldIdx) => {
    const ni = newIdxById.get(id);
    if (ni !== undefined) m.set(oldIdx, ni);
  });
  return m;
}

/** Re-key a Record<number, T>; entries without a mapping are dropped. */
export function remapIndexRecord<T>(
  rec: Record<number, T>,
  mapping: Map<number, number>,
): Record<number, T> {
  const out: Record<number, T> = {};
  for (const [k, v] of Object.entries(rec)) {
    const ni = mapping.get(Number(k));
    if (ni !== undefined) out[ni] = v;
  }
  return out;
}

/** Apply the old→new order diff to every index-keyed slice of the session.
 *  Returns the input object unchanged (same reference) when the orders are
 *  identical — the common case. */
export function remapSessionIndices(
  s: SessionState,
  oldOrder: string[],
  newOrder: string[],
): SessionState {
  const identical =
    oldOrder.length === newOrder.length &&
    oldOrder.every((id, i) => id === newOrder[i]);
  if (identical) return s;

  const m = buildIndexMapping(oldOrder, newOrder);
  const assetBlocks = remapIndexRecord(s.assetBlocks, m);
  const currentAssetIdx = m.get(s.currentAssetIdx) ?? 0;
  return {
    ...s,
    currentAssetIdx,
    assetBlocks,
    // The `blocks` mirror always reflects assetBlocks[currentAssetIdx].
    blocks: assetBlocks[currentAssetIdx] ?? [],
    stsAssets: remapIndexRecord(s.stsAssets, m),
    customDurations: remapIndexRecord(s.customDurations, m),
    customVideoEnabled: remapIndexRecord(s.customVideoEnabled, m),
    freeMode: remapIndexRecord(s.freeMode, m),
    freeBlocks: remapIndexRecord(s.freeBlocks, m),
    freeActiveIdx: remapIndexRecord(s.freeActiveIdx, m),
  };
}

/** Combined asset-id order: predefined config assets followed by the
 *  session's custom assets. This is the key space every index map uses. */
export function combinedAssetIdOrder(
  configAssets: ReadonlyArray<{ id: string }> | undefined,
  customAssets: ReadonlyArray<{ id: string }>,
): string[] {
  return [
    ...(configAssets ?? []).map((a) => a.id),
    ...customAssets.map((a) => a.id),
  ];
}
